import queryString from 'query-string';
import { getQueryDatesFromPeriod } from 'context/QueryParams';
import { ICurrencyFormat, ISettingsResponse, ISpendByType } from 'utils/types';
import { colors } from '@alixpartners/ui-components';
import {
  CONTRIBUTION_API_TO_UI,
  CONTRIBUTION_UI_TO_API,
  IApplicationSpend,
  IChartData,
  IChartLegend,
  IChartOptionsConfig,
  IProductResponse,
  ISpendByProduct,
  ISpendByProductResponse,
} from './types';
import { groupBy, orderBy, uniq } from 'lodash';
import { CONTRIBUTIONS, DISPLAY_BY } from 'utils/constants';

export const getChartColor = (dimension: string) => {
  switch (dimension) {
    case 'Third Party':
    case 'ThirdParty':
      return '#032E45';
    case 'Personnel':
      return '#0696A6';
    case DISPLAY_BY.HEADCOUNT:
      return '#0696A6';
    case 'Revenue':
      return colors.chartMediumGreen;
    case 'Cost':
      return colors.chartDarkGrey;
    case PERCENTAGE_OF_COST:
      return colors.amber;
    default:
      return '#0696A6';
  }
};

const getGradientFill = (color: string) => {
  return {
    pattern: {
      path: {
        d: 'M -2 2 l 2 -2 M 0 5 l 5 -5 M 4 6 l 2 -2',
        stroke: 'white',
        strokeWidth: 1,
      },
      width: 5,
      height: 5,
      backgroundColor: color,
    },
  };
};

const filterByContributions = (items: any[], contributions?: string) => {
  if (!contributions) {
    return items;
  }
  return items.filter(
    (item) => item.contribution === CONTRIBUTION_UI_TO_API[contributions]
  );
};

// Method to fetch data with standard query params
export const getQueryParams = (
  params: { [key: string]: string | string[] },
  settings: ISettingsResponse
) => {
  if (settings && settings?.selectedYear) {
    const { startDate, endDate } = getQueryDatesFromPeriod(
      settings.selectedYear
    );

    const searchQuery = queryString.stringify({ startDate, endDate });
    let filtersQuery = '';
    if (Object.keys(params).length !== 0) {
      // Remove empty parameters from query
      const query = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v != null)
      );
      filtersQuery = `&${queryString.stringify(query, {
        arrayFormat: 'index',
      })}`;
    }
    return `${searchQuery}${filtersQuery}`;
  }
  return '';
};

const EMPTY_PRODUCT = {
  contribution: '',
  productName: '',
  productId: 0,
  fte: 0,
  headcount: 0,
  productRevenue: 0,
  spend: 0,
  totalItCost: 0,
};

const EMPTY_PRODUCT_SPEND = {
  applicationId: 0,
  applicationName: '',
  contribution: '',
  fte: 0,
  headcount: 0,
  percentageOfAllAppSpend: 0,
  percentageOfAllITSpend: 0,
  spend: 0,
  thirdPartySpend: 0,
  personnelSpend: 0,
};

const EMPTY_APPLICATION = {
  contribution: '',
  dim1: '',
  dim2: '',
  dimensionName: '',
  fte: 0,
  headcount: 0,
  percentageOfAllAppSpend: 0,
  percentageOfAllITSpend: 0,
  spend: 0,
  appsAmount: 0,
};
const OTHERS = 'Others';
export const NON_PRODUCT_COST = 'Non-Product Cost';
export const NON_APP_COST = 'Non-app cost';
export const PERCENTAGE_OF_COST = 'Cost as % of total IT cost';
const MAX_ITEMS_ON_CHART = 12;

export const getChartLegend = (): IChartLegend => ({
  title: '',
  items: [],
});

export const EMPTY_CHART_DATA: IChartData = {
  categories: [],
  legend: getChartLegend(),
  series: [],
  tableData: [],
};

export const getSeriesFromProducts = ({
  productsResponse,
  chartOptionsConfig,
}: {
  productsResponse?: IProductResponse;
  chartOptionsConfig: IChartOptionsConfig;
}): IChartData => {
  let legend = getChartLegend();
  if (!productsResponse) {
    return EMPTY_CHART_DATA;
  }
  const { nonProductCost, nonProductTotalItCost, productsSpend } =
    productsResponse;
  const {
    contributions,
    displayBy,
    revenueCheckbox,
    costCheckbox,
    costAsPercentageCheckbox,
  } = chartOptionsConfig;

  let aggregatedProducts = filterByContributions(productsSpend, contributions);
  const groupedById = groupBy(aggregatedProducts, 'productId');
  aggregatedProducts = Object.keys(groupedById).map((productId) => {
    return groupedById[productId].reduce((acc, product) => {
      return {
        ...acc,
        productId: product.productId,
        productName: product.productName,
        contribution: 'All Spend',
        fte: acc.fte + product.fte,
        headcount: acc.headcount + product.headcount,
        productRevenue: acc.productRevenue + product.productRevenue,
        spend: acc.spend + product.spend,
        totalItCost: acc.totalItCost + product.totalItCost,
      };
    }, EMPTY_PRODUCT);
  });
  const spendOrderProperty = revenueCheckbox ? 'productRevenue' : 'spend';
  const orderProperty =
    displayBy === DISPLAY_BY.HEADCOUNT ? 'headcount' : spendOrderProperty;
  const sortedProducts = orderBy(aggregatedProducts, orderProperty, 'desc');
  let categories = sortedProducts.map((product) => product.productName);
  let otherCategories: string[] = [];
  if (categories.length > MAX_ITEMS_ON_CHART) {
    const topCategories = categories.splice(0, MAX_ITEMS_ON_CHART - 1);
    otherCategories = categories;
    categories = [...topCategories, OTHERS];
  }
  const getSerieData = (
    propertyName: string,
    color: string,
    options: any = {}
  ) => {
    return categories.map((category, x) => {
      if (category === OTHERS) {
        return otherCategories.reduce(
          (acc, otherCategory) => {
            const item = sortedProducts.find(
              (it) => it.productName === otherCategory
            );
            return {
              ...acc,
              y: acc.y + (item ? item[propertyName] : 0),
            };
          },
          {
            ...options,
            name: OTHERS,
            color,
            productId: 0,
            isClickDisabled: true,
            y: 0,
            x,
          }
        );
      }
      const product = sortedProducts.find((pr) => pr.productName === category);
      return {
        ...options,
        name: product.productName,
        color,
        productId: product.productId,
        y: product[propertyName],
        x,
      };
    });
  };

  if (displayBy === DISPLAY_BY.HEADCOUNT) {
    return {
      categories: [...categories, NON_PRODUCT_COST],
      legend,
      series: [
        {
          type: 'column',
          color: getChartColor(DISPLAY_BY.HEADCOUNT),
          name: 'Headcount',
          data: getSerieData('headcount', getChartColor(DISPLAY_BY.HEADCOUNT), {
            title: 'Headcount',
          }),
        },
      ],
      tableData: aggregatedProducts.map((product) => ({
        ...product,
        name: product.productName,
      })),
    };
  }
  const revenueSerie = {
    type: 'column',
    color: colors.chartMediumGreen,
    name: 'Revenue',
    yAxis: 0,
    grouping: true,
    data: getSerieData('productRevenue', colors.chartMediumGreen, {
      title: 'Revenue',
    }),
  };
  const costSerie = {
    type: 'column',
    color: colors.chartDarkGrey,
    name: 'Cost',
    yAxis: 0,
    grouping: true,
    data: getSerieData('spend', colors.chartDarkGrey, {
      title: 'Cost',
    }),
  };
  const percentOfRevenueSerie = {
    type: 'line',
    yAxis: 1,
    name: PERCENTAGE_OF_COST,
    data: [
      ...getSerieData('totalItCost', colors.amber, {
        isPercentage: true,
        title: PERCENTAGE_OF_COST,
      }),
      {
        y: nonProductTotalItCost,
        x: categories.length,
        isPercentage: true,
      },
    ],
    color: colors.amber,
    grouping: true,
  };

  let series = [];
  legend.title = 'Products';
  if (revenueCheckbox) {
    series.push(revenueSerie);
    legend.items.push('Revenue');
  }
  if (costCheckbox) {
    series.push(costSerie);
    series.push({
      type: 'column',
      color: colors.chartDarkGrey,
      name: NON_PRODUCT_COST,
      grouping: false,
      yAxis: 0,
      cursor: undefined,
      data: [
        {
          name: NON_PRODUCT_COST,
          color: getGradientFill(colors.chartDarkGrey) as any,
          productId: 0,
          isClickDisabled: true,
          x: categories.length,
          y: nonProductCost,
        },
      ],
    });
    legend.items.push('Cost');
  }
  if (costAsPercentageCheckbox) {
    series.push(percentOfRevenueSerie);
    legend.items.push(PERCENTAGE_OF_COST);
  }
  return {
    categories: [...categories, NON_PRODUCT_COST],
    legend,
    series,
    tableData: [
      {
        name: NON_PRODUCT_COST,
        productRevenue: 0,
        spend: nonProductCost,
        totalItCost: nonProductTotalItCost,
        headcount: 0,
      },
      ...aggregatedProducts.map((product) => ({
        ...product,
        name: product.productName,
      })),
    ],
  };
};

export const getSeriesFromSpendByProduct = ({
  spendByProduct,
  chartOptionsConfig,
}: {
  spendByProduct?: ISpendByProductResponse;
  chartOptionsConfig: IChartOptionsConfig;
}): IChartData => {
  let legend = getChartLegend();

  if (!spendByProduct) {
    return EMPTY_CHART_DATA;
  }
  const { applicationsSpendByProduct, nonApplicationsSpendByProduct } =
    spendByProduct;
  const { contributions, displayBy } = chartOptionsConfig;
  const isHeadcount = displayBy === DISPLAY_BY.HEADCOUNT;
  const orderProperty = isHeadcount ? 'headcount' : 'spend';
  let applications = filterByContributions(
    applicationsSpendByProduct,
    contributions
  );
  let nonApplications = filterByContributions(
    nonApplicationsSpendByProduct,
    contributions
  );
  applications = orderBy(applications, orderProperty, 'desc');
  nonApplications = orderBy(nonApplications, 'contribution', 'desc');
  let categories = uniq(applications.map((app) => app.applicationName));

  let otherCategories: string[] = [];
  if (categories.length > MAX_ITEMS_ON_CHART) {
    const topCategories = categories.splice(0, MAX_ITEMS_ON_CHART - 1);
    categories = [...topCategories, OTHERS];
    otherCategories = categories;
  }

  const groupedById = groupBy(applications, 'applicationId');
  const is2Series = Object.keys(groupedById).some((applicationId) => {
    const apps = groupedById[applicationId];
    return uniq(apps.map((app) => app.contribution)).length === 2;
  });
  const groupedByIdForTable: { [key: string]: any[] } = {
    ...groupedById,
    0: nonApplications,
  };
  const tableData = Object.keys(groupedByIdForTable).map((applicationId) => {
    const apps = groupedByIdForTable[applicationId];
    return apps.reduce(
      (acc, application) => ({
        ...acc,
        name: application.applicationName,
        thirdPartySpend:
          acc.thirdPartySpend +
          (application.contribution === 'ThirdParty' ? application.spend : 0),
        personnelSpend:
          acc.personnelSpend +
          (application.contribution === CONTRIBUTIONS.PERSONNEL
            ? application.spend
            : 0),
        headcount: acc.headcount + application.headcount,
        spend: acc.spend + application.spend,
        fte: acc.fte + application.fte,
        percentageOfAllAppSpend:
          acc.percentageOfAllAppSpend + application.percentageOfAllAppSpend,
        percentageOfAllITSpend:
          acc.percentageOfAllITSpend + application.percentageOfAllITSpend,
      }),
      {
        applicationName: '',
        thirdPartySpend: 0,
        personnelSpend: 0,
        headcount: 0,
        spend: 0,
        fte: 0,
        percentageOfAllAppSpend: 0,
        percentageOfAllITSpend: 0,
      }
    );
  });
  let series: any[] = [];

  const getAggregatedApps = (groupedApps: { [key: string]: any[] }) => {
    return Object.keys(groupedApps).map((id) =>
      groupedApps[id].reduce(
        (acc, application) => ({
          ...acc,
          contribution: application.contribution,
          applicationName: application.applicationName,
          headcount: acc.headcount + application.headcount,
          spend: acc.spend + application.spend,
          fte: acc.fte + application.fte,
          percentageOfAllAppSpend:
            acc.percentageOfAllAppSpend + application.percentageOfAllAppSpend,
          percentageOfAllITSpend:
            acc.percentageOfAllITSpend + application.percentageOfAllITSpend,
        }),
        EMPTY_PRODUCT_SPEND
      )
    );
  };

  const getSerieData = (
    items: any[],
    propertyName: string,
    color: string,
    options: any = {}
  ) => {
    return categories.map((category) => {
      if (category === OTHERS) {
        return otherCategories.reduce(
          (acc, otherCategory) => {
            const item = items.find(
              (it) => it?.applicationName === otherCategory
            );
            return {
              ...acc,
              y:
                acc.y +
                (item ? item[propertyName as keyof IApplicationSpend] : 0),
            };
          },
          {
            ...options,
            name: OTHERS,
            color,
            applicationId: 0,
            y: 0,
          }
        );
      }

      const item = items.find(
        (application) => application?.applicationName === category
      );
      return {
        ...options,
        name: item?.applicationName,
        contribution: item?.contribution,
        color:
          item?.applicationName === NON_APP_COST
            ? getGradientFill(getChartColor(item?.contribution))
            : getChartColor(item?.contribution),
        cursor: undefined,
        productId: item?.applicationId,
        y: item ? item[propertyName as keyof ISpendByProduct] : 0,
      };
    });
  };
  if (is2Series) {
    categories.push(NON_APP_COST);
    const groupedByContribution = groupBy(
      [...applications, ...nonApplicationsSpendByProduct],
      'contribution'
    );
    series = Object.keys(groupedByContribution).map((contribution) => {
      const contributionApps = groupedByContribution[contribution];
      const groupedById = groupBy(contributionApps, 'applicationId');
      applications = getAggregatedApps(groupedById);
      const nonAppCostSerie = nonApplicationsSpendByProduct.find(
        (item) => item.contribution === contribution
      );
      const serieData = getSerieData(
        [...applications, nonAppCostSerie],
        orderProperty,
        getChartColor(contribution),
        { title: contribution }
      );
      return {
        name: contribution,
        color: getChartColor(contribution),
        data: serieData,
      };
    });
    legend = {
      title: 'Applications',
      items: ['Third Party', 'Personnel'],
    };
  } else {
    const appContributions = uniq(applications.map((app) => app.contribution));
    const groupedById = groupBy(applications, 'applicationId');
    applications = getAggregatedApps(groupedById);
    const serieDataSeries = getSerieData(
      applications,
      orderProperty,
      getChartColor(CONTRIBUTIONS.PERSONNEL),
      {
        isColumnInTooltip: true,
      }
    );
    let serieData = serieDataSeries;
    nonApplications.forEach((nonApplication) => {
      const serie = {
        name: `${nonApplication.applicationName} ${
          CONTRIBUTION_API_TO_UI[nonApplication.contribution]
        }`,
        color: getGradientFill(getChartColor(nonApplication.contribution)),
        contribution: nonApplication.contribution,
        productId: 0,
        isClickDisabled: true,
        isColumnInTooltip: true,
        y: nonApplication[orderProperty],
      };
      serieData.push(serie);
      const categoryName = `${nonApplication.applicationName} ${
        nonApplications.length === 2
          ? CONTRIBUTION_API_TO_UI[nonApplication.contribution]
          : ''
      }`;
      categories.push(categoryName);
    });
    series = [
      {
        data: serieData,
      },
    ];
    legend = {
      title: 'Applications',
      items: appContributions,
    };
  }
  return {
    categories,
    legend,
    series,
    tableData,
  };
};

export const getSeriesFromSpendByApplication = ({
  spendByApplication,
  chartOptionsConfig,
}: {
  spendByApplication?: IApplicationSpend[];
  chartOptionsConfig: IChartOptionsConfig;
}): IChartData => {
  let legend = getChartLegend();

  if (!spendByApplication || spendByApplication.length === 0) {
    return EMPTY_CHART_DATA;
  }
  const { contributions, displayBy } = chartOptionsConfig;
  const orderProperty =
    displayBy === DISPLAY_BY.HEADCOUNT ? 'headcount' : 'spend';
  let applications = filterByContributions(spendByApplication, contributions);
  applications = orderBy(applications, orderProperty, 'desc');
  let categories = uniq(applications.map((app) => app.dim1));

  let otherCategories: string[] = [];
  if (categories.length > MAX_ITEMS_ON_CHART) {
    const topCategories = categories.splice(0, MAX_ITEMS_ON_CHART - 1);
    categories = [...topCategories, OTHERS];
    otherCategories = categories;
  }

  const groupedByDim1 = groupBy(applications, 'dim1');
  const is2Series = Object.keys(groupedByDim1).some((dim1) => {
    const apps = groupedByDim1[dim1];
    return uniq(apps.map((app) => app.contribution)).length === 2;
  });
  let series: any[] = [];

  const getAggregatedApps = (groupedApps: { [key: string]: any[] }) => {
    return Object.keys(groupedApps).map((dim1) =>
      groupedApps[dim1].reduce(
        (acc, application) => ({
          ...acc,
          contribution: application.contribution,
          dim1: application.dim1,
          headcount: acc.headcount + application.headcount,
          spend: acc.spend + application.spend,
          appsAmount: acc.appsAmount + 1,
          dimensionName: application.dimensionName,
          percentageOfAllAppSpend:
            acc.percentageOfAllAppSpend + application.percentageOfAllAppSpend,
          percentageOfAllITSpend:
            acc.percentageOfAllITSpend + application.percentageOfAllITSpend,
        }),
        EMPTY_APPLICATION
      )
    );
  };

  const getSerieData = (
    items: any[],
    propertyName: string,
    color: string,
    options: any = {}
  ) => {
    return categories.map((category) => {
      if (category === OTHERS) {
        return otherCategories.reduce(
          (acc, otherCategory) => {
            const item = items.find((it) => it.dim1 === otherCategory);
            return {
              ...acc,
              enableMouseTracking: false,
              y:
                acc.y +
                (item ? item[propertyName as keyof IApplicationSpend] : 0),
            };
          },
          {
            ...options,
            name: OTHERS,
            color,
            productId: 0,
            isClickDisabled: true,
            y: 0,
          }
        );
      }

      const item = items.find((application) => application.dim1 === category);
      return {
        ...options,
        name: item?.dim1,
        color: getChartColor(item?.contribution),
        productId: item?.applicationId,
        y: item ? item[propertyName as keyof ISpendByProduct] : 0,
        isClickDisabled: item?.dimensionName !== ISpendByType.SERVICE_TOWER,
      };
    });
  };
  if (is2Series) {
    const groupedByContribution = groupBy(applications, 'contribution');
    series = Object.keys(groupedByContribution).map((contribution) => {
      const contributionApps = groupedByContribution[contribution];
      const groupedByDim1 = groupBy(contributionApps, 'dim1');
      applications = getAggregatedApps(groupedByDim1);

      return {
        name: contribution,
        color: getChartColor(contribution),
        data: getSerieData(
          applications,
          orderProperty,
          getChartColor(contribution),
          { title: contribution }
        ),
      };
    });
    legend = {
      title: 'Service towers L1 / Job roles',
      items: ['Third Party', 'Personnel'],
    };
  } else {
    const groupedByDim1 = groupBy(applications, 'dim1');
    applications = getAggregatedApps(groupedByDim1);

    series = [
      {
        data: getSerieData(
          applications,
          orderProperty,
          getChartColor(CONTRIBUTIONS.PERSONNEL),
          {
            isColumnInTooltip: true,
          }
        ),
      },
    ];
    const appContributions = uniq(
      applications.map((application) => application.contribution)
    );
    if (appContributions.length === 2) {
      legend = {
        title: 'Service towers L1 / Job roles',
        items: ['Third Party', 'Personnel'],
      };
    } else {
      const contribution = appContributions[0];
      legend = {
        title: contribution === 'Personnel' ? 'Job roles' : 'Service towers L1',
        items: [contribution === 'Personnel' ? 'Personnel' : 'Third Party'],
      };
    }
  }
  const tableData = applications.map((application) => ({
    ...application,
    name: application.dim1,
    thirdPartySpend:
      application.contribution === CONTRIBUTIONS.PERSONNEL
        ? 0
        : application.spend,
    personnelSpend:
      application.contribution === CONTRIBUTIONS.PERSONNEL
        ? application.spend
        : 0,
  }));

  return {
    categories,
    legend,
    series,
    tableData,
  };
};

export const getSeriesFromSpendByApplicationL2 = ({
  spendByApplication,
  chartOptionsConfig,
}: {
  spendByApplication?: IApplicationSpend[];
  chartOptionsConfig: IChartOptionsConfig;
  format: ICurrencyFormat;
}): IChartData => {
  if (!spendByApplication || spendByApplication.length === 0) {
    return EMPTY_CHART_DATA;
  }
  const { contributions, displayBy, level3 } = chartOptionsConfig;
  const orderProperty =
    displayBy === DISPLAY_BY.HEADCOUNT ? 'headcount' : 'spend';
  const filteredApplications = filterByContributions(
    spendByApplication,
    contributions
  ).filter((application) => application.dim1 === level3);
  const sortedApplication = orderBy(
    filteredApplications,
    orderProperty,
    'desc'
  );
  let categories = sortedApplication.map((application) => application.dim2);
  let otherCategories: string[] = [];
  if (categories.length > MAX_ITEMS_ON_CHART) {
    const topCategories = categories.splice(0, MAX_ITEMS_ON_CHART - 1);
    categories = [...topCategories, OTHERS];
    otherCategories = categories;
  }
  const getSerieData = (propertyName: string) => {
    return categories.map((category) => {
      if (category === OTHERS) {
        return otherCategories.reduce(
          (acc, otherCategory) => {
            const application = sortedApplication.find(
              (app) => app.dim2 === otherCategory
            );
            return {
              ...acc,
              y:
                acc.y +
                (application
                  ? (application[
                      propertyName as keyof IApplicationSpend
                    ] as number)
                  : 0),
            };
          },
          {
            name: OTHERS,
            color: getChartColor(CONTRIBUTIONS.THIRD_PARTY),
            productId: 0,
            isClickDisabled: true,
            isColumnInTooltip: true,
            y: 0,
          }
        );
      }

      const application = sortedApplication.find(
        (application) => application.dim2 === category
      );

      return {
        name: application?.dim2 || '',
        color: getChartColor(
          application?.contribution || CONTRIBUTIONS.THIRD_PARTY
        ),
        y: application ? application[orderProperty] : 0,
        isClickDisabled: true,
        isColumnInTooltip: true,
      };
    });
  };

  const appContributions = uniq(
    sortedApplication.map((application) => application.contribution)
  );
  const tableData = sortedApplication.map((application) => ({
    ...application,
    name: application.dim2,
    thirdPartySpend:
      application.contribution === CONTRIBUTIONS.PERSONNEL
        ? 0
        : application.spend,
    personnelSpend:
      application.contribution === CONTRIBUTIONS.PERSONNEL
        ? application.spend
        : 0,
  }));

  return {
    categories,
    legend: {
      title: 'Service towers L2',
      items: appContributions,
    },
    series: [
      {
        data: getSerieData(orderProperty),
      },
    ],
    tableData,
  };
};
